import React, { useState, useEffect } from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { getQueue, syncQueue } from '../utils/offlineQueue';

export default function OfflineSyncBanner() {
  const { token } = useAuth();
  const { toast } = useToast();
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(getQueue().length);

  useEffect(() => {
    let alive = true;
    const refresh = () => alive && setPending(getQueue().length);
    const trySync = async () => {
      if (!token) return;
      const res = await syncQueue(token);
      if (alive && res && res.applied > 0) {
        toast({ title: 'Offline punches synced', description: `${res.applied} punch(es) recorded with their original times.` });
      }
      refresh();
    };
    const onOnline = () => { setOnline(true); trySync(); };
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('ht-offline-queue', refresh);
    trySync();
    const iv = setInterval(trySync, 60000);
    return () => {
      alive = false;
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('ht-offline-queue', refresh);
      clearInterval(iv);
    };
  }, [token]);

  if (online && pending === 0) return null;

  const banner = (online && pending > 0) || !online ? (
    <div
      className="flex items-center gap-2 px-4 py-2 text-sm font-medium"
      style={{
        background: online ? 'rgba(30,136,229,0.15)' : 'rgba(255,160,0,0.15)',
        color: online ? '#90CAF9' : '#FFCA7A',
        borderBottom: `1px solid ${online ? 'rgba(30,136,229,0.35)' : 'rgba(255,160,0,0.35)'}`,
      }}
      data-testid="offline-sync-banner"
    >
      {online ? <RefreshCw className="w-4 h-4 animate-spin" /> : <WifiOff className="w-4 h-4" />}
      {online
        ? `Back online — syncing ${pending} saved punch${pending === 1 ? '' : 'es'}…`
        : `You're offline — clock punches are saved on this device and will sync automatically${pending > 0 ? ` (${pending} pending)` : ''}.`}
    </div>
  ) : null;

  return (
    <>
      {banner}
      {pending > 0 && (
        <button
          onClick={() => { if (navigator.onLine && token) syncQueue(token).then(() => setPending(getQueue().length)); }}
          data-testid="offline-pending-badge"
          title={online ? 'Tap to sync now' : 'Will sync automatically when back online'}
          className="fixed bottom-4 left-4 z-50 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg"
          style={{
            background: online ? 'rgba(30,136,229,0.9)' : 'rgba(255,160,0,0.9)',
            color: '#0B1220',
          }}
        >
          <WifiOff className="w-3.5 h-3.5" />
          {pending} punch{pending === 1 ? '' : 'es'} waiting to sync
        </button>
      )}
    </>
  );
}
